import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import { LAST_30_DAYS_RANGE_PRESET_LOCAL_FULL } from 'cio/utils/date-picker';

/*
  Creates an abstract route that can be used in your deliveries routes. This
  parent controller is responsible for resetting the filters set on the controller
  adding pagination reset observers and preventing scrolling when the route activates.

  Example:

 ```javascript
  export default DeliveriesRoute({
    setupController(controller, model) {
      controller.set('campaign', model.get('id'));

     this._super(...arguments);
    }
  });
 ```

  The above example sets the campaign property on the controller. If you're
  extending the DeliveriesController, this essentially overrides the default
  of null - letting you have a preselected campaign for your list.

  You can essentially set defaults on the Controller, but this helps avoid
  two-way binding using computed and avoids issues when the resetController hook
  runs and tries to set campaign and if you have used the readOnly binding.
*/

class DeliveriesRoute extends Route {
  @service messageSettings;
  @service store;

  scrollOnActivate = false;
  setDefaultTimeRange = true;

  async afterModel(model, transition) {
    await this.messageSettings.loadMessagingLimit.perform();

    return super.afterModel(model, transition);
  }

  setupController(controller) {
    super.setupController(...arguments);

    if (this.setDefaultTimeRange) {
      if (controller.startTs == null && controller.endTs == null) {
        let range = LAST_30_DAYS_RANGE_PRESET_LOCAL_FULL.getRange();
        controller.setProperties({
          startTs: range.start.unix(),
          endTs: range.end.unix(),
        });
      }
    }

    let queryParams = controller.get('queryParams').filter((param) => !['page', 'continuation'].includes(param));

    queryParams.forEach((key) => {
      // eslint-disable-next-line ember/no-observers
      controller.addObserver(key, controller, controller.resetPagination);
    });

    controller.fetchDeliveries.perform();
  }

  resetController(controller, isExiting) {
    super.resetController(...arguments);

    let queryParams = controller.get('queryParams').filter((param) => !['page', 'continuation'].includes(param));

    queryParams.forEach((key) => {
      controller.removeObserver(key, controller, controller.resetPagination);
    });

    if (isExiting) {
      controller.send('clearFilters');
      controller.set('deliveries', []);
      controller.set('startTs', null);
      controller.set('endTs', null);
    }
  }
}

/**
   @class DeliveriesRoute
   @extends Ember.Route
   @public
*/
export default DeliveriesRoute;
